import type { CaseStudy, Locale } from "./types"

/**
 * Case studies migrated from the approved portfolio copy.
 *
 * Content rule: no invented outcomes or metrics. Only claims present in the
 * original approved site copy are retained (e.g. "significant daily time
 * savings"). Confidential projects keep their confidentiality.
 */
export const cases: CaseStudy[] = [
  {
    id: "de-limburger",
    pageType: "case-study",
    slug: { nl: "de-limburger", en: "de-limburger" },
    clientName: "De Limburger",
    title: {
      nl: "Automatisering van regionale contentdistributie voor De Limburger",
      en: "Automating regional content distribution for De Limburger",
    },
    description: {
      nl: "Hoe een intelligent systeem het dagelijkse handmatige plaatsen van content over tientallen doelgroepen verving bij het grootste regionale dagblad van Limburg.",
      en: "How an intelligent system replaced the daily manual placement of content across dozens of audiences at Limburg's leading regional daily.",
    },
    h1: {
      nl: "Geautomatiseerde contentdistributie voor De Limburger",
      en: "Automated content distribution for De Limburger",
    },
    location: { nl: "Maastricht", en: "Maastricht" },
    industry: { nl: "Uitgeverij / regionale media", en: "Publishing / regional media" },
    summary: {
      nl: "Voor een toonaangevende publicatie ontwikkelden wij een slimme automatiseringsoplossing voor regionale contentdistributie die het dagelijkse handmatige plaatsingsproces verving.",
      en: "For a leading publication we developed a smart automation solution for regional content distribution that replaced the daily manual placement process.",
    },
    problem: {
      nl: "Redactionele content moest dagelijks handmatig geplaatst worden over tientallen specifieke doelgroepen. Dat kostte het team elke dag veel tijd en was foutgevoelig: bij de groeiende hoeveelheid content was handmatig plaatsen niet meer opschaalbaar.",
      en: "Editorial content had to be placed manually every day across dozens of specific audiences. This consumed significant team time each day and was error-prone; with growing content volume, manual placement could no longer scale.",
    },
    solution: {
      nl: "We verkenden eerst de beperkingen van de platforms waarop gepubliceerd wordt, en bouwden daarna een geautomatiseerde workflow met geavanceerde categorisatielogica. Het systeem herkent automatisch de relevantie van content en plaatst deze op de juiste locaties — zonder dat er een hand aan te pas komt.",
      en: "We first explored the limitations of the publishing platforms involved, then built an automated workflow with advanced categorisation logic. The system automatically identifies content relevance and places it in the right locations — without manual intervention.",
    },
    outcome: {
      nl: "Het dagelijkse proces van handmatige plaatsing is vervangen door een intelligent systeem. Het team realiseert dagelijks aanzienlijke tijdsbesparing en kan zich richten op waardevoller werk.",
      en: "The daily manual placement process has been replaced by an intelligent system. The team achieves significant daily time savings and can focus on more valuable work.",
    },
    image: "/portfolio/de_limburger.webp",
    gradientColor: "rgba(26, 25, 18, 0.8)",
    relatedServiceIds: ["ai-automation", "ai-development"],
    relatedKnowledgeIds: ["ai-agents-vs-automatisering", "van-ai-poc-naar-productie"],
  },
  {
    id: "burgerkracht",
    pageType: "case-study",
    slug: { nl: "burgerkracht", en: "burgerkracht" },
    clientName: "Burgerkracht",
    title: {
      nl: "Conversationele navigatieassistent voor Burgerkracht",
      en: "Conversational navigation assistant for Burgerkracht",
    },
    description: {
      nl: "Een AI-navigatieassistent met kennisbank-gebaseerde retrieval die bezoekers van een groot communityplatform automatisch doorverwijst naar relevante groepen en informatie.",
      en: "An AI navigation assistant with knowledge-base-driven retrieval that automatically refers visitors of a large community platform to relevant groups and information.",
    },
    h1: {
      nl: "AI-navigatieassistent voor Burgerkracht",
      en: "AI navigation assistant for Burgerkracht",
    },
    location: { nl: "Sittard", en: "Sittard" },
    industry: { nl: "Maatschappelijke organisatie", en: "Social sector organisation" },
    summary: {
      nl: "Voor een omvangrijk communityplatform ontwikkelden wij een intelligente navigatieassistent die bezoekers via natuurlijke gesprekken naar de juiste informatie leidt.",
      en: "For a large community platform we developed an intelligent navigation assistant that guides visitors to the right information through natural conversation.",
    },
    problem: {
      nl: "Bezoekers van het platform werden vaak overweldigd door de hoeveelheid beschikbare groepen en informatie. De juiste groep of pagina vinden voelde als zoeken naar een speld in een hooiberg, waardoor mensen vastliepen of afhaakten.",
      en: "Visitors were often overwhelmed by the amount of available groups and information. Finding the right group or page felt like searching for a needle in a haystack, causing people to get stuck or drop off.",
    },
    solution: {
      nl: "We implementeerden een geavanceerde conversationele interface die natuurlijke gesprekken voert, de situatie van de gebruiker begrijpt en vervolgens automatisch doorverwijst naar de meest relevante groepen of pagina's. Het systeem gebruikt moderne taalmodellen in combinatie met een kennisbank-gebaseerde retrievalarchitectuur (RAG), waarmee het dynamisch relevante informatie ophaalt en verwerkt.",
      en: "We implemented an advanced conversational interface that conducts natural conversations, understands the user's situation and then automatically refers them to the most relevant groups or pages. The system leverages modern language models combined with a knowledge-base-driven retrieval architecture (RAG), allowing it to dynamically fetch and process relevant information.",
    },
    outcome: {
      nl: "Het resultaat is een toegankelijker platform waar gebruikers zich direct op hun gemak voelen en waar relevante informatie en groepen vindbaar zijn via gewoon Nederlands te praten met de assistent.",
      en: "The result is a more accessible platform where users immediately feel at home and where relevant information and groups are findable simply by talking to the assistant in plain language.",
    },
    image: "/portfolio/burgerkracht.webp",
    gradientColor: "rgba(0, 0, 0, 0.6)",
    relatedServiceIds: ["ai-agents", "ai-knowledge-systems"],
    relatedKnowledgeIds: ["rag-chatbot-laten-bouwen", "ai-agent-laten-bouwen"],
  },
  {
    id: "den-engelsen",
    pageType: "case-study",
    slug: { nl: "den-engelsen", en: "den-engelsen" },
    clientName: "Den Engelsen",
    title: {
      nl: "Technische kennis toegankelijk maken voor monteurs van Den Engelsen",
      en: "Making technical knowledge accessible for Den Engelsen mechanics",
    },
    description: {
      nl: "Een intern AI-platform dat technische documentatie en voertuigspecifieke informatie voor monteurs direct vindbaar maakt bij een dealer en servicepartner van bedrijfswagens.",
      en: "An internal AI platform that makes technical documentation and vehicle-specific information instantly findable for mechanics at a commercial vehicle dealer and service partner.",
    },
    h1: {
      nl: "Technische kennis binnen handbereik voor monteurs van Den Engelsen",
      en: "Technical knowledge at hand for Den Engelsen mechanics",
    },
    location: { nl: "Venlo", en: "Venlo" },
    industry: { nl: "Bedrijfswagens / werkplaats", en: "Commercial vehicles / workshop" },
    summary: {
      nl: "Voor een dealer en servicepartner van bedrijfswagens ontwikkelden wij een platform dat technische kennis voor monteurs toegankelijk maakt: van voertuiggegevens tot de juiste werkinstructie, gevonden met een vraag in eigen woorden.",
      en: "For a commercial vehicle dealer and service partner we developed a platform that makes technical knowledge accessible to mechanics: from vehicle data to the right work instruction, found with a question in their own words.",
    },
    problem: {
      nl: "Technische informatie over voertuigen is omvangrijk en verspreid over verschillende systemen en duizenden documenten. Monteurs verloren tijd met zoeken, en welke instructie precies van toepassing is op een specifiek voertuig was niet altijd direct duidelijk.",
      en: "Technical vehicle information is extensive and spread across different systems and thousands of documents. Mechanics lost time searching, and it was not always immediately clear which instruction applied to a specific vehicle.",
    },
    solution: {
      nl: "We bouwden een intern platform dat technische documentatie en voertuiggegevens op één plek samenbrengt. Monteurs zoeken een voertuig op en zien direct de informatie die bij die specifieke uitvoering hoort. Een AI-zoekfunctie begrijpt vragen in de taal van de werkplaats, inclusief vaktermen en onderdeelnummers, en zet de meest relevante instructies bovenaan. Feedback van monteurs uit de praktijk gebruiken we om de zoekresultaten continu te verbeteren.",
      en: "We built an internal platform that brings technical documentation and vehicle data together in one place. Mechanics look up a vehicle and immediately see the information that belongs to that specific configuration. An AI search function understands questions in the language of the workshop, including trade terms and part numbers, and puts the most relevant instructions first. Feedback from mechanics in daily practice is used to continuously improve the search results.",
    },
    outcome: {
      nl: "Het resultaat is één plek waar monteurs de technische kennis vinden die ze nodig hebben, afgestemd op het voertuig waar ze aan werken. Zo gaat er minder tijd naar zoeken en meer naar het werk zelf.",
      en: "The result is a single place where mechanics find the technical knowledge they need, tailored to the vehicle they are working on. Less time goes into searching and more into the work itself.",
    },
    image: "/portfolio/den_engelsen.webp",
    gradientColor: "rgba(8, 57, 85, 0.8)",
    relatedServiceIds: ["ai-knowledge-systems", "ai-development"],
    relatedKnowledgeIds: ["ai-kennisbank-voor-bedrijven", "ai-integreren-bedrijfssoftware"],
  },
  {
    id: "quantitative-trading",
    pageType: "case-study",
    slug: { nl: "quantitative-trading", en: "quantitative-trading" },
    clientName: "Institutionele trading-partner",
    title: {
      nl: "AI-gedreven marktintelligentiesysteem voor institutionele trading",
      en: "AI-driven market intelligence system for institutional trading",
    },
    description: {
      nl: "Een compleet marktintelligentiesysteem dat realtime financiële informatie verwerkt en autonoom posities beheert op grote marktindices.",
      en: "A complete market intelligence system that processes real-time financial information and autonomously manages positions on major market indices.",
    },
    h1: {
      nl: "Quantitatief tradingplatform",
      en: "Quantitative trading platform",
    },
    industry: { nl: "Financiële dienstverlening", en: "Financial services" },
    summary: {
      nl: "Voor een institutionele trading klant ontwikkelden wij een AI-gedreven marktintelligentiesysteem dat enorme hoeveelheden mondiale financiële informatie omzet in bruikbare handelsbeslissingen.",
      en: "For an institutional trading client we developed an AI-powered market intelligence system that transforms massive volumes of global financial information into actionable trading decisions.",
    },
    problem: {
      nl: "De uitdaging was het omzetten van enorme hoeveelheden mondiale financiële informatie in bruikbare handelsbeslissingen. Handmatige analyse kon de hoeveelheid en snelheid van realtime marktinformatie niet bijbenen.",
      en: "The challenge was transforming massive volumes of global financial information into actionable trading decisions. Manual analysis could not keep up with the volume and speed of real-time market information.",
    },
    solution: {
      nl: "We bouwden een complete oplossing die een geavanceerd analyse-AI-model combineert met een geautomatiseerd tradingmodel dat strategieën uitvoert op belangrijke marktindices. Het systeem verwerkt real-time marktinformatie en beheert autonoom posities.",
      en: "We built a comprehensive solution combining an advanced analysis AI model with an automated trading model that executes strategies on major market indices. The system processes real-time market information and autonomously manages positions.",
    },
    outcome: {
      nl: "Het systeem maakt geavanceerde algoritmische handel op institutionele schaal mogelijk: marktanalyse en positiebeheer lopen volledig geautomatiseerd en continu door.",
      en: "The system enables sophisticated algorithmic trading at institutional scale: market analysis and position management run fully automated and continuously.",
    },
    image: "/portfolio/trading_project.webp",
    gradientColor: "rgba(10, 25, 47, 0.85)",
    relatedServiceIds: ["ai-development", "ai-agents"],
    relatedKnowledgeIds: ["van-ai-poc-naar-productie", "ai-agent-bedrijfsdata"],
  },
]

export function getCase(id: string): CaseStudy | undefined {
  return cases.find((c) => c.id === id)
}

export function getCaseBySlug(slug: string, locale: Locale): CaseStudy | undefined {
  return cases.find((c) => c.slug[locale] === slug)
}
